import React from 'react';
import { useLocation } from 'react-router-dom';
import type { ComponentType } from 'react';
import { cn } from '@heroui/react';
import { useMobileNavigate } from '@/hooks/useMobileNavigate';

export interface TabBarItem {
  /**
   * 路由路径
   */
  path: string;
  /**
   * 图标（未激活状态）
   */
  icon: ComponentType<{ className?: string; size?: number; style?: React.CSSProperties }>;
  /**
   * 图标（激活状态），可选，默认使用 icon
   */
  activeIcon?: ComponentType<{ className?: string; size?: number; style?: React.CSSProperties }>;
  /**
   * 标签文字
   */
  label: string;
  /**
   * 是否匹配子路由（如 /home 匹配 /home/detail）
   * @default false
   */
  matchSubRoutes?: boolean;
}

export interface TabBarProps {
  /**
   * TabBar 配置项
   */
  items: TabBarItem[];
  /**
   * 激活时的颜色
   * @default 'var(--primary)'
   */
  activeColor?: string;
  /**
   * 未激活时的颜色
   * @default 'var(--default-500)'
   */
  inactiveColor?: string;
  /**
   * 自定义类名
   */
  className?: string;
  /**
   * 是否固定在底部
   * @default true
   */
  fixed?: boolean;
}

/**
 * 移动端底部导航栏组件
 * 
 * 特性：
 * - ✅ 支持路由导航
 * - ✅ 自动高亮当前路由
 * - ✅ 支持安全区域（iOS notch）
 * - ✅ 响应式设计
 * - ✅ 支持图标和文字
 */
export function TabBar({
  items,
  activeColor = 'var(--primary)',
  inactiveColor = 'var(--default-500)',
  className,
  fixed = true,
}: TabBarProps) {
  const location = useLocation();
  const { go } = useMobileNavigate();

  const isActive = (item: TabBarItem): boolean => {
    if (item.path === location.pathname) {
      return true;
    }
    if (item.matchSubRoutes && location.pathname.startsWith(item.path)) {
      return true;
    }
    return false;
  };

  const handleClick = (item: TabBarItem) => {
    if (location.pathname !== item.path) {
      go(item.path);
    }
  };

  return (
    <div
      className={cn(
        'w-full bg-background border-t border-border',
        fixed && 'fixed bottom-0 left-1/2 -translate-x-1/2 z-50',
        className
      )}
      style={{
        maxWidth: 'var(--app-max-width)',
        paddingBottom: 'env(safe-area-inset-bottom)',
        paddingLeft: 'env(safe-area-inset-left)',
        paddingRight: 'env(safe-area-inset-right)',
        height: 'calc(var(--tabbar-height, 50px) + env(safe-area-inset-bottom))',
      }}
    >
      <div className="flex items-center justify-around h-[var(--tabbar-height,50px)]">
        {items.map((item) => {
          const active = isActive(item);
          const Icon = active && item.activeIcon ? item.activeIcon : item.icon;
          const color = active ? activeColor : inactiveColor;

          return (
            <button
              key={item.path}
              type="button"
              onClick={() => handleClick(item)}
              className={cn(
                'flex flex-col items-center justify-center flex-1 h-full',
                'transition-colors duration-200',
                'active:opacity-70'
              )}
              aria-label={item.label}
            >
              <Icon
                size={24}
                style={{ color }}
                className="mb-1"
              />
              <span
                className="text-xs"
                style={{ color }}
              >
                {item.label}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
